import { useCallback, useEffect, useRef, useState } from 'react';
import { grabLayer } from '../../utils/fieldFrame';
import { sampleVideo } from '../camera/useFocusMeter';
import {
  LAYERS, ZStackCancelled, ZStackRefused, isFlat, runStepping, runSweep, stepDistances,
} from '../../utils/zstack/engine';
import { MAX_LAYERS, frameSharpness, select, tileSharpness } from '../../utils/zstack/select';

const RUNNING = new Set(['stepping', 'sweeping', 'selecting']);

/**
 * A Z-stack from the live camera in `videoRef` (docs/FIELD_AI_VISION_SPEC.md
 * 10.3; utils/zstack/): `controls` is useCameraControls()'s answer.
 *
 * `path` is 'auto' -- focus stepping, where the camera offers a manual focus
 * and a distance (`canStep`), the person's choice by default -- or 'manual', a
 * hand sweep. `settings` {path, layers (3-25), span ('whole' | 'around'), quick}
 * last while the camera is open. `setQuick(3 | 4 | 5)` is the quick stack: that
 * many layers around the focus now, or a hand sweep's sharpest that many, so it
 * works on a camera with no focus control; `setQuick(null)` goes back to the
 * full stack, and setting the layers or the range by hand leaves it.
 *
 * `run.phase`: idle; stepping {done, total} or sweeping {ms, frames}; then
 * review {stack}, or failed {reason: tooFew | failed}. start(), stop() (a
 * sweep ends early and is reviewed), cancel() (nothing kept), keep() -> the
 * stack, discard().
 *
 * Where stepping does not work on this phone -- the camera refuses the
 * manual focus, takes it without moving the lens, or every layer shows the
 * same plane -- the stack goes on by hand at once: sweeping {fallback:
 * 'refused' | 'ignored' | 'flat'}, the panel saying what to do, and
 * `stepBlocked` names why stepping is off until the camera is opened again.
 * A refusal after LAYERS.min layers keeps those layers instead. A sweep holds
 * the focus where the automatic focus found it (controls.holdFocus), so the
 * moving phone or the turned knob moves the plane.
 *
 * A kept stack is {path, order, layers: [{blob, url, focus_distance, t_ms,
 * sharpness, tile_wins, kept}] in order, cover (its sharpest), rejected,
 * settings, startedAt, taken}; its JPEGs are the hosts' to upload
 * (utils/zstack/bundle.js). The focus is put back as it was after stepping.
 * `ring` leaves an eyepiece's dark ring out of the scores (the microscope).
 * Tests hand in `grab`, `sampler`, `sleep` and `now`.
 */
export function useZStack(videoRef, controls, {
  maxSide = 2048, quality = 0.88, ring = false, grab = null, sampler = sampleVideo, sleep, now,
} = {}) {
  const caps = controls.caps;
  const [stepBlocked, setStepBlocked] = useState(null);
  // A camera opened again (or another one) is tried again.
  useEffect(() => { setStepBlocked(null); }, [caps]);
  const canStep = !stepBlocked && !!caps?.manualFocus && !!caps?.focusDistance;
  const [settings, setSettingsState] = useState({ path: 'auto', layers: LAYERS.default, span: 'whole', quick: null });
  const path = settings.path === 'auto' && canStep ? 'auto' : 'manual';
  const [run, setRunState] = useState({ phase: 'idle' });
  const runRef = useRef(run);
  const setRun = useCallback((next) => {
    runRef.current = typeof next === 'function' ? next(runRef.current) : next;
    setRunState(runRef.current);
  }, []);
  const signal = useRef(null);
  const stopping = useRef(false);
  const canvas = useRef(null);
  const mounted = useRef(true);

  const revoke = (stack) => stack?.layers?.forEach(l => l.url && typeof URL.revokeObjectURL === 'function'
    && URL.revokeObjectURL(l.url));

  useEffect(() => () => {
    mounted.current = false;
    if (signal.current) signal.current.aborted = true;
    revoke(runRef.current.stack);
  }, []);

  const setSettings = useCallback((change) => setSettingsState(s => ({
    ...s, ...change, quick: 'layers' in change || 'span' in change ? null : s.quick,
  })), []);
  const setQuick = useCallback((n) => setSettingsState(s => (n
    ? { ...s, quick: n, layers: n, span: 'around' }
    : { ...s, quick: null, layers: LAYERS.default, span: 'whole' })), []);

  const start = useCallback(async () => {
    if (RUNNING.has(runRef.current.phase) || !caps) return;
    revoke(runRef.current.stack);
    const aborted = { aborted: false };
    signal.current = aborted;
    stopping.current = false;
    const startedAt = new Date();
    const sampleTiles = () => {
      if (!canvas.current && typeof document !== 'undefined') canvas.current = document.createElement('canvas');
      let frame = null;
      try { frame = sampler(videoRef.current, canvas.current); } catch { frame = null; }
      return frame ? tileSharpness(frame.gray, frame.w, frame.h, { ring }) : null;
    };
    // Looked up when a stack starts, not on every render (tests stand the module in).
    const grabOne = () => (grab || grabLayer)(videoRef.current, { maxSide, quality, gain: controls.gain, ring });
    const timing = { ...(sleep ? { sleep } : {}), ...(now ? { now } : {}) };
    const sweep = async (fallback = null) => {
      setRun({ phase: 'sweeping', ms: 0, frames: 0, ...(fallback ? { fallback } : {}) });
      // Where stepping did not work, no manual hold either.
      const held = await controls.holdFocus?.({ manual: !fallback && !stepBlocked });
      try {
        return await runSweep({
          sample: sampleTiles, grab: grabOne, stopped: () => stopping.current, signal: aborted, ...timing,
          onTick: t => mounted.current && setRun(r => (r.phase === 'sweeping' ? { ...r, ms: t.ms, frames: t.frames } : r)),
        });
      } finally {
        if (held) await controls.restoreFocus();
      }
    };
    let frames;
    let taken = path;
    let fallback = null;
    try {
      if (path === 'auto') {
        const around = settings.span === 'around'
          ? (controls.focus?.distance ?? caps.focusDistance.value) : null;
        const distances = stepDistances(caps.focusDistance, settings.layers, { around });
        setRun({ phase: 'stepping', done: 0, total: distances.length });
        try {
          frames = await runStepping({
            distances, applyFocus: controls.applyFocus, grab: grabOne, signal: aborted, ...timing,
            readFocus: controls.focusNow,
            sample: () => { const t = sampleTiles(); return t ? frameSharpness(t) : null; },
            onLayer: (done, total) => mounted.current && setRun(r => ({ ...r, done, total })),
          });
        } catch (e) {
          if (!(e instanceof ZStackRefused)) throw e;
          if (e.reason === 'refused' && e.frames.length >= LAYERS.min) frames = e.frames;
          else fallback = e.reason;
        } finally {
          await controls.restoreFocus();
        }
        if (!fallback && isFlat(frames) && select(frames, { maxLayers: MAX_LAYERS }).layers.length < 2) {
          fallback = 'flat';
        }
        if (fallback) {
          if (!mounted.current || aborted.aborted) throw new ZStackCancelled();
          setStepBlocked(fallback);
          taken = 'manual';
          frames = await sweep(fallback);
        }
      } else {
        frames = await sweep();
      }
    } catch (e) {
      if (!mounted.current) return;
      if (e instanceof ZStackCancelled) setRun({ phase: 'idle' });
      else setRun({ phase: 'failed', reason: 'failed' });
      return;
    }
    if (!mounted.current || aborted.aborted) {
      if (mounted.current) setRun({ phase: 'idle' });
      return;
    }
    setRun({ phase: 'selecting' });
    const order = taken === 'auto' ? 'focus_distance' : 'time';
    const maxLayers = settings.quick || MAX_LAYERS;
    const chosen = select(frames, { order, maxLayers });
    if (chosen.layers.length < 2) {
      setRun({ phase: 'failed', reason: 'tooFew', taken: frames.length, rejected: chosen.rejected,
               ...(fallback ? { fallback } : {}) });
      return;
    }
    const layers = chosen.layers.map((l) => {
      const frame = frames[l.index];
      return {
        blob: frame.blob, focus_distance: frame.focus_distance ?? null, t_ms: frame.t_ms,
        sharpness: l.sharpness, tile_wins: l.tile_wins, kept: l.kept,
        url: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(frame.blob) : null,
      };
    });
    setRun({
      phase: 'review',
      stack: {
        path: taken, order, layers, cover: chosen.cover, rejected: chosen.rejected, startedAt, taken: frames.length,
        settings: { path: taken, layers: taken === 'auto' ? settings.layers : null,
                    span: taken === 'auto' ? settings.span : null, max_layers: maxLayers,
                    ...(settings.quick ? { quick: settings.quick } : {}),
                    ...(fallback ? { fallback } : {}) },
      },
    });
  }, [caps, path, stepBlocked, settings, controls, grab, sampler, sleep, now, maxSide, quality, ring, videoRef, setRun]);

  const stop = useCallback(() => { stopping.current = true; }, []);
  const cancel = useCallback(() => {
    if (signal.current) signal.current.aborted = true;
    if (!RUNNING.has(runRef.current.phase)) setRun({ phase: 'idle' });
  }, [setRun]);
  /** The stack to keep (its JPEGs; the review's previews let go), and back to idle. */
  const keep = useCallback(() => {
    const { stack } = runRef.current;
    if (!stack) return null;
    revoke(stack);
    setRun({ phase: 'idle' });
    return { ...stack, layers: stack.layers.map(({ url, ...layer }) => layer) };
  }, [setRun]);
  const discard = useCallback(() => {
    revoke(runRef.current.stack);
    setRun({ phase: 'idle' });
  }, [setRun]);

  return {
    path, canStep, stepBlocked, settings, setSettings, setQuick, run, start, stop, cancel, keep, discard,
    busy: RUNNING.has(run.phase), active: run.phase !== 'idle',
    device: () => ({ label: controls.trackLabel?.() || '', facing: 'environment' }),
  };
}
